import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { ModalContainerComponent } from '@components-library/tb-modal-manager/modal-container-component/modal-container.component'
import { Workspace } from '@core/models'
import { NavigatorService } from '@services/navigator.service'
import { SchemaFacadeService, WorkspaceFacadeService } from '@services/store-facade'
import { UntilDestroy } from '@ngneat/until-destroy'
import { LogService } from '@services/logger/log.service'
import { map, take, tap } from 'rxjs/operators'
import { combineLatest } from 'rxjs'
import {
    AddWorkspaceComponent,
    WorkspaceDialogData,
} from '@app/feature/header/workspace-controls/add-workspace-dialog-content/add-workspace.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { TranslocoModule } from '@jsverse/transloco'
import { MatDividerModule } from '@angular/material/divider'
import { WorkspaceComponent } from '../workspace/workspace.component'
import { AsyncPipe } from '@angular/common'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { TbDividerComponent } from '@components-library/tb-divider/tb-divider.component'

@UntilDestroy()
@Component({
    selector: 'app-workspaces-list',
    templateUrl: './workspaces-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ModalLayoutComponent,
        WorkspaceComponent,
        MatDividerModule,
        TranslocoModule,
        TbButtonComponent,
        AsyncPipe,
        TbDividerComponent,
    ],
})
export class WorkspacesListComponent extends ModalContainerComponent {
    private readonly schemaFacadeService = inject(SchemaFacadeService)
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    private readonly navigatorService = inject(NavigatorService)
    private readonly logService = inject(LogService)

    workspaces$ = combineLatest([
        this.workspaceFacadeService.selectAllWorkspaces$,
        this.workspaceFacadeService.selectGlobalWorkspace$,
    ]).pipe(
        map(([workspaces, global]) => {
            const nonGlobal = workspaces.filter((workspace) => workspace !== global)

            return { global, nonGlobal }
        }),
        tap((value) => {
            if (!value.global) {
                this.logService.error(new Error('no global workspace'))
            }
        }),
    )
    selectedWorkspace$ = this.workspaceFacadeService.selectSelectedWorkspace$

    selectWorkspace(workspace: Workspace) {
        this.workspaceFacadeService.setSelectedWorkspaceGuid(workspace.guid)
        this.close()
        this.schemaFacadeService.selectSelectedTableSchemaGuid$
            .pipe(take(1))
            .subscribe((tableSchemaGuid) => {
                if (tableSchemaGuid) {
                    this.navigatorService.goToSystemObject(tableSchemaGuid, workspace.guid)
                    return
                }

                this.navigatorService.goToWorkspace(workspace.guid)
            })
    }

    openWorkspaceCreateDialog() {
        this.openWorkspaceDialog()
    }

    openWorkspaceEditDialog(workspace: Workspace) {
        this.openWorkspaceDialog({
            updateData: {
                name: workspace.name.value,
                icon: workspace.icon.value,
                color: workspace.color.value,
            },
            workspace,
        })
    }

    private openWorkspaceDialog(data?: WorkspaceDialogData) {
        return this.modalManagerService
            .openDialog<AddWorkspaceComponent, WorkspaceDialogData>({
                component: AddWorkspaceComponent,
                data: data,
            })
            .pipe(take(1))
    }
}
